import type { InputHTMLAttributes, TextareaHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { cn } from '../lib/cn';
import './Input.css';

export interface FieldProps {
  label: ReactNode;
  /** Texto de ajuda sob o controle. */
  help?: ReactNode;
  /** Mensagem de erro — quando presente marca o controle como inválido. */
  error?: ReactNode;
  required?: boolean;
  className?: string;
  /** Recebe os ids a ligar no controle (`id`, `aria-describedby`, `aria-invalid`). */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/** Wrapper de campo: rótulo + ajuda + erro, com ARIA cabeada ao controle. */
export function Field({
  label,
  help,
  error,
  required,
  className,
  children,
}: FieldProps): JSX.Element {
  const base = useId();
  const id = `${base}-control`;
  const helpId = `${base}-help`;
  const errId = `${base}-err`;
  const describedBy =
    [error ? errId : null, help ? helpId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('cf-field', className)}>
      <label className="cf-field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="cf-field__req" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {help && !error && (
        <p className="cf-field__help" id={helpId}>
          {help}
        </p>
      )}
      {error && (
        <p className="cf-field__error" id={errId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  /** Fonte monoespaçada (protocolos, códigos). */
  mono?: boolean;
}

export function Input({ invalid, mono, className, ...rest }: InputProps): JSX.Element {
  return (
    <input
      className={cn('cf-input', mono && 'cf-input--mono', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function Textarea({ invalid, className, ...rest }: TextareaProps): JSX.Element {
  return (
    <textarea
      className={cn('cf-input', 'cf-textarea', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}
